import React, { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridPagination, GridRowSelectionModel } from '@mui/x-data-grid';

import CorporateEventsService, { Registration } from '~/services/CorporateEventsService';
import { formatDateSlashes } from '~/services/TimeService';

interface RegistrationsListProps {
  eventId?: string;
  eventName?: string;
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  submitted: 'success',
  pending: 'warning',
  cancelled: 'error',
  declined: 'error',
};

export default function RegistrationsList({ eventId, eventName }: RegistrationsListProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const handleExport = async () => {
    if (!eventId) return;
    setIsExporting(true);
    try {
      const blob = await CorporateEventsService.downloadRegistrationsExport(eventId);
      const slug = eventName ? eventName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'event';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${slug}-registrations.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      enqueueSnackbar('Failed to export registrations. Please try again.', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: ({ ids }: { ids: Array<string> }) => CorporateEventsService.removeRegistrations(eventId!, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'registrations', eventId] });
      enqueueSnackbar('Registrations removed successfully', { variant: 'success' });
      setSelectedRows([]);
      setConfirmDeleteOpen(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error removing registrations: ${error.message}`, { variant: 'error' });
    },
  });

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate({ ids: selectedRows as Array<string> });
  };

  const handleCloseConfirmDelete = () => {
    if (deleteMutation.isPending) return;
    setConfirmDeleteOpen(false);
  };

  const {
    data: registrations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['corporate-events', 'registrations', eventId],
    queryFn: () => {
      if (!eventId) {
        return Promise.resolve([]);
      }
      return CorporateEventsService.getRegistrationsByEventId(eventId);
    },
    enabled: !!eventId,
  });

  const columns: Array<GridColDef<Registration>> = [
    {
      field: 'firstName',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => [row.firstName, row.middleName, row.lastName].filter(Boolean).join(' '),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={STATUS_COLORS[params.value] ?? 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'willAttend',
      headerName: 'Will Attend',
      width: 130,
      renderCell: (params) => {
        if (params.value == null) {
          return null;
        }
        return <Chip label={params.value ? 'Yes' : 'No'} size="small" color={params.value ? 'success' : 'error'} />;
      },
    },
    {
      field: 'mobileNumber',
      headerName: 'Mobile',
      width: 160,
      valueGetter: (value, row) => {
        if (!row.mobileNumber) {
          return '';
        }
        return row.mobilePrefix ? `${row.mobilePrefix} ${row.mobileNumber}` : row.mobileNumber;
      },
    },
    {
      field: 'firstSubmittedAt',
      headerName: 'Submitted At',
      width: 130,
      valueFormatter: (value) => formatDateSlashes(value),
    },
    {
      field: 'lastUpdatedAt',
      headerName: 'Last Updated',
      width: 130,
      valueFormatter: (value) => formatDateSlashes(value),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          {selectedRows.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
              disabled={deleteMutation.isPending}
            >
              Delete Selected ({selectedRows.length})
            </Button>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={!eventId || isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export Registrations'}
        </Button>
      </Stack>

      {error && (
        <Box mb={2} p={2} sx={{ backgroundColor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          Error loading registrations: {error instanceof Error ? error.message : 'Unknown error'}
        </Box>
      )}

      <DataGrid
        loading={isLoading}
        rows={registrations}
        columns={columns}
        pagination
        initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
        pageSizeOptions={[10, 25, 50, 100]}
        getRowId={(row) => row.id}
        slots={{ pagination: GridPagination }}
        autoHeight
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={setSelectedRows}
      />

      <Dialog open={confirmDeleteOpen} onClose={handleCloseConfirmDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Registration Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to permanently delete {selectedRows.length} registration
            {selectedRows.length !== 1 ? 's' : ''}. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDelete} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Registrations'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
